import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Wallet, TrendingUp, Percent, Coins } from "lucide-react";

/**
 * Section « Rémunération » — utilisée dans le formulaire de création/édition
 * d'un collaborateur (page Collaborateurs), et dans le dossier détaillé
 * (DossierCollaborateur). Interface admin uniquement (l'appelant doit vérifier).
 *
 * Modèle métier :
 *   - Fixe forfaitaire mensuel : `remuneration_fixed_ht` (€ HT, optionnel)
 *   - Variable au choix : `remuneration_variable_mode` in {"none", "percentage", "amount"}
 *       • "percentage" → `remuneration_variable_percentage` (% du CA HT sur la période)
 *       • "amount"     → `remuneration_variable_amount`     (€ HT par vente)
 *   - Un collaborateur peut cumuler fixe + variable, ou n'avoir que l'un des deux,
 *     ou ni l'un ni l'autre.
 */
export default function RemunerationSection({ form, setForm, readOnly = false }) {
  const mode = form.remuneration_variable_mode || "none";
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div className="space-y-5" data-testid="remuneration-section">
      {/* Rémunération fixe */}
      <section className="border border-zinc-200 rounded-sm p-4 bg-zinc-50/50">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded-sm bg-emerald-600 text-white inline-flex items-center justify-center">
            <Wallet size={14} />
          </div>
          <div>
            <div className="text-sm font-semibold text-zinc-900">Rémunération fixe forfaitaire</div>
            <div className="text-[11px] font-mono text-zinc-500">Montant mensuel HT — laisser vide si aucun fixe.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-[240px]">
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="1000.00"
              value={form.remuneration_fixed_ht ?? ""}
              onChange={(e) => update({ remuneration_fixed_ht: e.target.value })}
              readOnly={readOnly}
              disabled={readOnly}
              data-testid="remun-fixed-input"
              className="pr-14"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-500 pointer-events-none">
              € HT
            </span>
          </div>
          <span className="text-xs font-mono text-zinc-500">/ mois</span>
        </div>
      </section>

      {/* Rémunération variable */}
      <section className="border border-zinc-200 rounded-sm p-4 bg-zinc-50/50">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded-sm bg-orange-500 text-white inline-flex items-center justify-center">
            <TrendingUp size={14} />
          </div>
          <div>
            <div className="text-sm font-semibold text-zinc-900">Rémunération variable</div>
            <div className="text-[11px] font-mono text-zinc-500">Indexée sur les ventes / le CA — cumulable avec le fixe.</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { key: "none", label: "Aucune", icon: null },
            { key: "percentage", label: "% du CA HT", icon: <Percent size={12} /> },
            { key: "amount", label: "€ / vente", icon: <Coins size={12} /> },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => update({ remuneration_variable_mode: opt.key })}
              disabled={readOnly}
              className={`h-9 rounded-sm border text-xs font-mono uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition-all
                ${mode === opt.key ? "bg-orange-500 border-orange-500 text-white shadow-sm" : "border-zinc-300 text-zinc-700 hover:bg-white"}
                ${readOnly ? "opacity-70 cursor-not-allowed" : ""}`}
              data-testid={`remun-mode-${opt.key}`}
            >
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>

        {mode === "percentage" && (
          <div className="flex items-center gap-2 mt-2">
            <div className="relative flex-1 max-w-[240px]">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="5.00"
                value={form.remuneration_variable_percentage ?? ""}
                onChange={(e) => update({ remuneration_variable_percentage: e.target.value })}
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="remun-var-percentage-input"
                className="pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-500 pointer-events-none">
                %
              </span>
            </div>
            <span className="text-xs font-mono text-zinc-500">du CA HT généré par période</span>
          </div>
        )}
        {mode === "amount" && (
          <div className="flex items-center gap-2 mt-2">
            <div className="relative flex-1 max-w-[240px]">
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="15.00"
                value={form.remuneration_variable_amount ?? ""}
                onChange={(e) => update({ remuneration_variable_amount: e.target.value })}
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="remun-var-amount-input"
                className="pr-14"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-500 pointer-events-none">
                € HT
              </span>
            </div>
            <span className="text-xs font-mono text-zinc-500">/ vente réalisée</span>
          </div>
        )}
      </section>

      {/* Notes */}
      <section>
        <Label className="label-uppercase mb-1.5 block">Notes internes (facultatif)</Label>
        <Textarea
          value={form.remuneration_notes ?? ""}
          onChange={(e) => update({ remuneration_notes: e.target.value })}
          readOnly={readOnly}
          disabled={readOnly}
          placeholder="Ex : prime d'ancienneté, révision annuelle, spécificités contractuelles…"
          rows={3}
          data-testid="remun-notes"
          className="font-mono text-xs"
        />
      </section>
    </div>
  );
}

/**
 * Normalise l'objet ``form`` en payload API — convertit les strings en nombres
 * et gère le mode "none" (⇒ chaîne vide côté back pour marquer l'absence).
 * À utiliser uniquement quand l'utilisateur courant est admin.
 */
export function serializeRemuneration(form) {
  const toFloat = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };
  const mode = form.remuneration_variable_mode || "none";
  return {
    remuneration_fixed_ht: toFloat(form.remuneration_fixed_ht),
    remuneration_variable_mode: mode === "none" ? "" : mode,
    remuneration_variable_percentage:
      mode === "percentage" ? toFloat(form.remuneration_variable_percentage) : null,
    remuneration_variable_amount:
      mode === "amount" ? toFloat(form.remuneration_variable_amount) : null,
    remuneration_notes: form.remuneration_notes || "",
  };
}
